/**
 * ========================================
 * AUTH CONTEXT
 * ========================================
 * Global authentication state management
 * Provides auth state and methods to all components
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { authService, SessionConflictError } from '@services/auth/index';
import { subscriptionService } from '@services/api/subscription.service';
import { APIError as APIErrorClass } from '@/services/api/error-handler';
import { trialService, type TrialStatus } from '@/services/trial';
import type { User, LoginCredentials, RegisterData, AuthContextType, SessionConflict } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Auth Provider Component
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionConflict, setSessionConflict] = useState<SessionConflict | null>(null);
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null);

  // Activity-based token refresh: track last interaction, refresh before expiry when active
  const lastActivityRef = useRef(Date.now());
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * Fetch the current subscription and derive trial status from it -- the
   * source of truth is UserSubscription.status/trialEndsAt (backend), not
   * any field on the User object itself.
   */
  const refreshTrialStatus = useCallback(async () => {
    try {
      const sub = await subscriptionService.getCurrent();
      setTrialStatus(trialService.getTrialStatus(sub));
    } catch (err) {
      console.error('Failed to load subscription for trial status:', err);
      setTrialStatus(trialService.getTrialStatus(null));
    }
  }, []);

  /**
   * Initialize auth state on mount
   * Check if user is already logged in
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUser = authService.getUser();
        if (storedUser && authService.isAuthenticated()) {
          setUser(storedUser);
          await refreshTrialStatus();
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [refreshTrialStatus]);

  /**
   * Login handler
   */
  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);
    setSessionConflict(null);
    try {
      const response = await authService.login(credentials);
      setUser(response.user);
      await refreshTrialStatus();
    } catch (err: any) {
      if (err instanceof SessionConflictError) {
        setSessionConflict({ otherDevice: err.otherDevice, lastSeenAt: err.lastSeenAt });
        setIsLoading(false);
        return;
      }
      // Get user-friendly message from APIError
      let errorMessage = 'Login failed';
      
      if (err instanceof APIErrorClass) {
        errorMessage = err.getUserMessage();
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [refreshTrialStatus]);

  /**
   * Register handler
   * Note: Registration does NOT auto-login the user
   * User must manually sign in after creating account
   */
  const register = useCallback(async (data: RegisterData) => {
    setIsLoading(true);
    setError(null);
    try {
      // Call register - this only creates the account, does NOT set user state
      await authService.register(data);
      // Success! User will navigate to login page manually
      // No need to set user state here
    } catch (err: any) {
      // Get user-friendly message from APIError
      let errorMessage = 'Registration failed';
      
      if (err instanceof APIErrorClass) {
        // Check if there are field-specific validation errors
        if (err.details && Object.keys(err.details).length > 0) {
          // Field label map for better readability
          const fieldLabelMap: Record<string, string> = {
            email: 'Email',
            username: 'Username',
            password: 'Password',
            first_name: 'First Name',
            last_name: 'Last Name',
            phone_number: 'Phone Number',
            gender: 'Gender',
            date_of_birth: 'Date of Birth',
            profile_image: 'Profile Image',
            address: 'Address',
            city: 'City',
            state: 'State',
            country: 'Country',
            pincode: 'PIN Code',
          };

          // Format field-specific errors
          const fieldErrors = Object.entries(err.details)
            .map(([field, msgs]: [string, any]) => {
              // Use mapped label or convert field name to readable format
              const fieldLabel = fieldLabelMap[field] || 
                field
                  .split('_')
                  .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
              
              // Get error message
              const errorMsg = Array.isArray(msgs) 
                ? msgs.join(', ')
                : typeof msgs === 'string' ? msgs : JSON.stringify(msgs);
              
              return `${fieldLabel}: ${errorMsg}`;
            })
            .join('\n');
          
          errorMessage = fieldErrors;
        } else {
          errorMessage = err.getUserMessage();
        }
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Rethrow the error so components can detect failure
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Register activity listeners — update lastActivityRef on any user interaction
  useEffect(() => {
    const mark = () => { lastActivityRef.current = Date.now(); };
    const opts = { passive: true } as const;
    window.addEventListener('mousemove', mark, opts);
    window.addEventListener('keydown', mark, opts);
    window.addEventListener('click', mark, opts);
    window.addEventListener('scroll', mark, opts);
    window.addEventListener('touchstart', mark, opts);
    return () => {
      window.removeEventListener('mousemove', mark);
      window.removeEventListener('keydown', mark);
      window.removeEventListener('click', mark);
      window.removeEventListener('scroll', mark);
      window.removeEventListener('touchstart', mark);
    };
  }, []);

  // Token auto-refresh: every 2 min, if user is active and token expires within 4 min → refresh
  useEffect(() => {
    if (!user) {
      if (refreshTimerRef.current) { clearInterval(refreshTimerRef.current); refreshTimerRef.current = null; }
      return;
    }

    refreshTimerRef.current = setInterval(async () => {
      const idleSec = (Date.now() - lastActivityRef.current) / 1000;
      if (idleSec > 300) return; // idle >5 min — skip refresh

      const expiresIn = authService.getTokenExpiresInSeconds();
      if (expiresIn > 0 && expiresIn < 240) { // expires in <4 min
        try {
          await authService.refreshToken();
        } catch {
          // Silent — user will be prompted to re-login when they next make an API call
        }
      }
    }, 120_000); // check every 2 minutes

    return () => {
      if (refreshTimerRef.current) { clearInterval(refreshTimerRef.current); refreshTimerRef.current = null; }
    };
  }, [user]);

  /**
   * Profile update handler -- persists via authService.updateUser (which also writes the
   * correctly-unwrapped user to localStorage) and, critically, updates the LIVE `user` state
   * here too. Callers that only called authService.updateUser() directly (Header.tsx,
   * MyProfile.tsx) never touched this context's state, so the header/name/every other
   * useAuth() consumer kept showing the pre-edit (or, before the unwrap fix, corrupted) user
   * until a full reload re-read localStorage -- which is what looked like "switches to a
   * generic 'User' and the page says not authenticated" right after a save.
   */
  const updateProfile = useCallback(async (userId: string, data: Partial<User>): Promise<User> => {
    const updatedUser = await authService.updateUser(userId, data);
    setUser(updatedUser);
    return updatedUser;
  }, []);

  /**
   * Establish a session from a token an endpoint OTHER than the normal login flow already
   * minted (currently: school self-enrollment, which creates the account and signs the
   * student in as one step -- unlike register(), which deliberately does not auto-login).
   */
  const establishSession = useCallback(async (token: string, userData: Partial<User>) => {
    const response = await authService.establishSession(token, userData);
    setUser(response.user);
    await refreshTrialStatus();
  }, [refreshTrialStatus]);

  /**
   * Logout handler
   */
  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setError(null);
    setTrialStatus(null);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
    setSessionConflict(null);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    sessionConflict,
    login,
    register,
    establishSession,
    logout,
    clearError,
    updateProfile,
    trialStatus,
    canAccess: trialStatus?.canAccess ?? false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth Hook
 * Use this in components to access authentication state and methods
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
